import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { mediaMin } from "styles/media";
import DennisImgSrc from "./dennis.png";
import MadisonImgSrc from "./madison.png";

const FluidWrapper = styled.section`
  width: 100%;
  background-color: ${({ theme }) => theme.palette.gray80};
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 140px 16px 180px;

  ${mediaMin.xs`
    padding: 140px 32px 180px;
  `}
`;

const TestimonialSectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 500;
  line-height: 1.2;
  text-align: center;
  color: ${({ theme }) => theme.palette.black};

  ${mediaMin.xs`
    font-size: 32px;
  `}

  ${mediaMin.sm`
    font-size: 38px;
  `}

  ${mediaMin.md`
    font-size: 38px;
  `}

  ${mediaMin.lg`
    font-size: 48px;
  `}
`;

const SectionTitleWrapper = styled.div`
  margin-bottom: 22px;
`;

const Description = styled.div`
  font-size: 18px;
  text-align: center;
  color: ${({ theme }) => theme.palette.black};
  margin-bottom: 80px;
  line-height: 1.4;

  ${mediaMin.sm`
    font-size: 20px;
  `}
`;

const TalkBoxesWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-gap: 80px;

  ${mediaMin.xs`
    grid-gap: 120px;
    max-width: 640px;
  `}

  ${mediaMin.md`
    grid-gap: 32px;
    max-width: 1040px;
    grid-template-rows: initial;
    grid-template-columns: 1fr 1fr;
    margin-top: 80px;
  `}

  ${mediaMin.lg`
    margin-top: 120px;
  `}
`;

const TalkBox = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaMin.xs`
    flex-direction: row;
    gap: 24px;
  `}

  ${mediaMin.md`
    flex-direction: column;
  `}
`;

const SecondTalkBox = styled(TalkBox)`
  ${mediaMin.xs`
    flex-direction: row-reverse;
    gap: 24px;
  `}

  ${mediaMin.md`
    flex-direction: column;
    align-items: initial;
  `}
`;

const PictureWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 160px;
  height: 160px;
  margin-bottom: 24px;

  ${mediaMin.xs`
    width: 172px;
    height: 172px;
    margin-bottom: 0;
  `}
`;

const Picture = styled.img`
  width: 100%;
  height: 100%;
`;

const TalkWrapper = styled.div`
  flex: 1;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.black};
  font-size: 18px;
`;

const Talk = styled.div`
  line-height: 1.5;
  margin-bottom: 24px;
  font-size: 16px;

  ${mediaMin.xs`
    font-size: 18px;
  `}
`;

const TalkerName = styled.div`
  font-weight: 500;
  margin-bottom: 4px;
  line-height: 1.4;
  font-size: 18px;
`;

const TalkerJob = styled.div`
  font-size: 16px;
  line-height: 1.4;
`;

export function TestimonialSection() {
  const { t } = useTranslation();
  return (
    <FluidWrapper>
      <Wrapper>
        <SectionTitleWrapper>
          <TestimonialSectionTitle color="black">
            {t("main.testimonialSection.mainCopy")}
          </TestimonialSectionTitle>
        </SectionTitleWrapper>
        <Description>{t("main.testimonialSection.subCopy")}</Description>
        <TalkBoxesWrapper>
          <TalkBox>
            <PictureWrapper>
              <Picture src={DennisImgSrc} />
            </PictureWrapper>
            <TalkWrapper>
              <Talk>{t("main.testimonialSection.part1.message")}</Talk>
              <TalkerName>{t("main.testimonialSection.part1.name")}</TalkerName>
              <TalkerJob>
                {t("main.testimonialSection.part1.job")}
                <a
                  rel="noreferrer"
                  target="_blank"
                  href="https://techstars.com/"
                >
                  Techstars
                </a>
              </TalkerJob>
            </TalkWrapper>
          </TalkBox>
          <SecondTalkBox>
            <PictureWrapper>
              <Picture src={MadisonImgSrc} />
            </PictureWrapper>
            <TalkWrapper>
              <Talk>{t("main.testimonialSection.part2.message")}</Talk>
              <TalkerName>{t("main.testimonialSection.part2.name")}</TalkerName>
              <TalkerJob>
                {t("main.testimonialSection.part2.job")}
                <a
                  rel="noreferrer"
                  target="_blank"
                  href="https://rosellastudio.com/"
                >
                  Rosella Studio
                </a>
              </TalkerJob>
            </TalkWrapper>
          </SecondTalkBox>
        </TalkBoxesWrapper>
      </Wrapper>
    </FluidWrapper>
  );
}
